import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import './ReservationForm.css';

function formatDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const FENETRE_JOURS = 60; // jusqu'à combien de jours à l'avance on propose une réservation

function ReservationForm() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [prestationId, setPrestationId] = useState('');
  const [prestations, setPrestations] = useState([]);
  const [capsuleId, setCapsuleId] = useState('');
  const [capsules, setCapsules] = useState([]);
  const [description, setDescription] = useState('');
  const [instagram, setInstagram] = useState('');
  const [depose, setDepose] = useState(''); // '' = pas encore choisi, 'oui' ou 'non'
  const [photos, setPhotos] = useState([]);
  const [message, setMessage] = useState(null);
  const [dateJour, setDateJour] = useState('');
  const [creneauxDisponibles, setCreneauxDisponibles] = useState([]);
  const [heureDebut, setHeureDebut] = useState('');
  const [chargementCreneaux, setChargementCreneaux] = useState(false);
  const navigate = useNavigate();

  // 📅 Jours réellement ouverts par l'admin (au moins un créneau disponible), sur la fenêtre de réservation
  const [joursDisponibles, setJoursDisponibles] = useState(new Set());
  const [chargementJours, setChargementJours] = useState(true);

  const aujourdHui = new Date();
  aujourdHui.setHours(0, 0, 0, 0);
  const finFenetre = new Date(aujourdHui);
  finFenetre.setDate(finFenetre.getDate() + FENETRE_JOURS);

  const [moisAffiche, setMoisAffiche] = useState(new Date(aujourdHui.getFullYear(), aujourdHui.getMonth(), 1));

  // 🔄 Récupère, une seule fois, tous les jours ouverts sur les prochains FENETRE_JOURS jours
  useEffect(() => {
    const debut = formatDateLocal(aujourdHui);
    const fin = formatDateLocal(finFenetre);
    fetch(`${API_URL}/creneaux?debut=${debut}&fin=${fin}`)
      .then(res => res.json())
      .then(data => {
        const jours = new Set(data.filter(c => c.disponible).map(c => c.jour));
        setJoursDisponibles(jours);
        setChargementJours(false);
      })
      .catch(err => {
        console.error("Erreur fetch jours disponibles :", err);
        setChargementJours(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔄 Récupère les créneaux (horaires) disponibles pour la date choisie
  useEffect(() => {
    if (!dateJour) {
      setCreneauxDisponibles([]);
      setHeureDebut('');
      return;
    }
    setChargementCreneaux(true);
    setHeureDebut('');
    fetch(`${API_URL}/creneaux?debut=${dateJour}&fin=${dateJour}`)
      .then(res => res.json())
      .then(data => {
        setCreneauxDisponibles(data.filter(c => c.disponible));
        setChargementCreneaux(false);
      })
      .catch(err => {
        console.error("Erreur fetch créneaux :", err);
        setChargementCreneaux(false);
      });
  }, [dateJour]);

  // 🔄 Récupère les prestations disponibles
  useEffect(() => {
    fetch(`${API_URL}/prestations`)
      .then(res => res.json())
      .then(data => {
        setPrestations(data);
      })
      .catch(err => console.error("Erreur fetch prestations :", err));
  }, []);

  // 🔄 Récupère les capsules disponibles
  useEffect(() => {
    fetch(`${API_URL}/capsules`)
      .then(res => res.json())
      .then(data => {
        setCapsules(data);
      })
      .catch(err => console.error("Erreur fetch capsules :", err));
  }, []);

  const moisPrecedentAutorise = () => {
    const moisMin = new Date(aujourdHui.getFullYear(), aujourdHui.getMonth(), 1);
    return moisAffiche > moisMin;
  };

  const moisSuivantAutorise = () => {
    const moisMax = new Date(finFenetre.getFullYear(), finFenetre.getMonth(), 1);
    return moisAffiche < moisMax;
  };

  const moisPrecedent = () => {
    if (!moisPrecedentAutorise()) return;
    setMoisAffiche(new Date(moisAffiche.getFullYear(), moisAffiche.getMonth() - 1, 1));
  };

  const moisSuivant = () => {
    if (!moisSuivantAutorise()) return;
    setMoisAffiche(new Date(moisAffiche.getFullYear(), moisAffiche.getMonth() + 1, 1));
  };

  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1; // lundi = 0
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const renderCalendrier = () => {
    const year = moisAffiche.getFullYear();
    const month = moisAffiche.getMonth();
    const daysInMonth = getDaysInMonth(moisAffiche);
    const firstDay = getFirstDayOfMonth(moisAffiche);

    const cells = [];
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="resa-cal-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDateLocal(date);
      const estDansLaFenetre = date >= aujourdHui && date <= finFenetre;
      const estOuvert = estDansLaFenetre && joursDisponibles.has(dateStr);
      const estSelectionne = dateJour === dateStr;

      cells.push(
        <button
          type="button"
          key={`day-${day}`}
          className={`resa-cal-day ${estOuvert ? 'ouvert' : 'ferme'} ${estSelectionne ? 'selectionne' : ''}`}
          disabled={!estOuvert}
          onClick={() => setDateJour(dateStr)}
        >
          {day}
        </button>
      );
    }

    return cells;
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!depose) {
      setMessage({ type: 'error', text: 'Merci de préciser si une dépose est à prévoir.' });
      return;
    }

    if (!dateJour) {
      setMessage({ type: 'error', text: 'Merci de choisir une date disponible.' });
      return;
    }

    if (!heureDebut) {
      setMessage({ type: 'error', text: 'Merci de choisir un horaire disponible.' });
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('phone', phone);
    formData.append('dateJour', dateJour);
    formData.append('heureDebut', heureDebut);
    formData.append('prestationId', prestationId);
    if (capsuleId) {
      formData.append('capsuleId', capsuleId);
    }
    formData.append('description', description);
    formData.append('instagram', instagram);
    formData.append('depose', depose === 'oui');
    photos.forEach(file => formData.append('photos', file));

    fetch(`${API_URL}/reservations`, {
      method: 'POST',
      body: formData
    })
    .then(async res => {
      if (!res.ok) {
        let errorMessage = `Échec de la réservation (HTTP ${res.status})`;
        try {
          const errorData = await res.clone().json();
          errorMessage = errorData.message || errorData.detail || errorData.title || errorData.error || errorMessage;
        } catch {
          const text = await res.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      return res.json();
    })
    .then(() => {
      setMessage({ type: 'success', text: 'Réservation confirmée 💅' });
      setName('');
      setPhone('');
      setDateJour('');
      setHeureDebut('');
      setPrestationId('');
      setCapsuleId('');
      setDescription('');
      setInstagram('');
      setDepose('');
      setPhotos([]);
    })
    .catch(err => {
      setMessage({ type: 'error', text: err.message });
    });
  };

  return (
    <div className="reservation-form-container">
      <form className="reservation-form" onSubmit={handleSubmit}>
        <h2>Réserver un rendez-vous</h2>

        <label>Prénom :</label>
        <input
          className="form-input"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Votre nom"
          required
        />

        <label>Téléphone :</label>
        <input
          className="form-input"
          type="tel"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="Votre numéro"
          required
        />

        <label>Instagram :</label>
        <input
          className="form-input"
          type="text"
          value={instagram}
          onChange={e => setInstagram(e.target.value)}
          placeholder="@votre_compte"
        />

        <label>Date de la prestation :</label>
        {chargementJours ? (
          <p className="form-hint">Chargement des disponibilités...</p>
        ) : (
          <div className="resa-calendar">
            <div className="resa-cal-header">
              <button type="button" onClick={moisPrecedent} disabled={!moisPrecedentAutorise()} className="resa-cal-nav">&lt;</button>
              <span>{monthNames[moisAffiche.getMonth()]} {moisAffiche.getFullYear()}</span>
              <button type="button" onClick={moisSuivant} disabled={!moisSuivantAutorise()} className="resa-cal-nav">&gt;</button>
            </div>
            <div className="resa-cal-grid">
              <div className="resa-cal-weekday">L</div>
              <div className="resa-cal-weekday">M</div>
              <div className="resa-cal-weekday">M</div>
              <div className="resa-cal-weekday">J</div>
              <div className="resa-cal-weekday">V</div>
              <div className="resa-cal-weekday">S</div>
              <div className="resa-cal-weekday">D</div>
              {renderCalendrier()}
            </div>
            {joursDisponibles.size === 0 && (
              <p className="form-hint">Aucune date disponible pour le moment, reviens un peu plus tard.</p>
            )}
          </div>
        )}

        {dateJour && (
          <>
            <label>Horaire :</label>
            {chargementCreneaux ? (
              <p className="form-hint">Chargement des créneaux...</p>
            ) : creneauxDisponibles.length === 0 ? (
              <p className="form-hint">Ce créneau vient d'être pris, merci de choisir une autre date.</p>
            ) : (
              <select
                className="form-select"
                value={heureDebut}
                onChange={e => setHeureDebut(e.target.value)}
                required
              >
                <option value="">-- Choisir un horaire --</option>
                {creneauxDisponibles.map(c => (
                  <option key={c.heure} value={c.heure}>{c.heure}</option>
                ))}
              </select>
            )}
          </>
        )}

        <label>Prestation : </label>
        <select
          className="form-select"
          value={prestationId}
          onChange={e => setPrestationId(e.target.value)}
          required
        >
          <option value="">-- Choisir une prestation --</option>
          {prestations.map(p => (
            <option key={p.id} value={p.id}>
                {[p.titre, p.description, p.prix != null ? `${p.prix} €` : null]
                  .filter(Boolean)
                  .join(' — ')}
            </option>
          ))}
        </select>

        <label>Dépose à prévoir ? (Attention, dépose de capsule américaines UNIQUEMENT, faire une dépose avant le rdv si c'est du gel ou autre)</label>
        <select
          className="form-select"
          value={depose}
          onChange={e => setDepose(e.target.value)}
          required
        >
          <option value="">-- Choisir --</option>
          <option value="oui">Oui</option>
          <option value="non">Non</option>
        </select>

        <label>Forme des capsules :</label>
        <select
          className="form-select"
          value={capsuleId}
          onChange={e => setCapsuleId(e.target.value)}
        >
          <option value="">-- Choisir une capsule --</option>
          {capsules.map(c => (
            <option key={c.id} value={c.id}>
              {c.titre}
            </option>
          ))}
        </select>

        <label>Photos d'inspiration :</label>
        <input
          className="form-input"
          type="file"
          accept="image/*"
          multiple
          onChange={e => setPhotos(Array.from(e.target.files))}
        />
        
        {photos.length > 0 && (
          <div className="photos-preview">
            <p className="preview-label">Photos sélectionnées ({photos.length}) :</p>
            <div className="preview-grid">
              {photos.map((photo, idx) => (
                <div key={idx} className="photo-item">
                  <img src={URL.createObjectURL(photo)} alt={`aperçu ${idx + 1}`} />
                  <p className="photo-name">{photo.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <label>Précisions :</label>
        <textarea
          className="form-textarea"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Décrivez ce que vous voulez (couleur, motif, etc.)"
        />

        <button type="submit" className="btn-grad">Réserver</button>
        <button type="button" className="btn-return" onClick={() => navigate('/')}>
          Retour à l'accueil
        </button>
      </form>
      {message && (
        <div className={`reservation-message ${message.type}`}>
            {message.text}
            {message.type === 'success' && (
              <button 
                type="button" 
                className="btn-return"
                onClick={() => navigate('/')}
              >
                Retour à l'accueil
              </button>
            )}
          </div>
      )}
    </div>
  );
}

export default ReservationForm;
